/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  AssetOption,
  ActionSuccess,
  Asset,
  Folder,
  Channel,
  Program,
  ProductPrice,
  MenuItem,
} from '@wit/vtv-x-foundation-lib';
import { SeasonsData } from 'core/store/ducks/pi/seasons';
import { StoreState } from 'store/store';
import * as Context from './context';

interface Payload {
  context: Context.PiContext;
}

interface PayloadUpdate extends Payload {
  data: Partial<PiRecord>;
}

export enum ActionType {
  UPDATE = 'programInfo/data/UPDATE',
}

// ACTIONS

type Update = ActionSuccess<ActionType.UPDATE, PayloadUpdate>;
type Actions = Update | Context.Actions;

const Actions = {
  update: (payload: PayloadUpdate): Update => ({ type: ActionType.UPDATE, payload }),
};

export const ActionCreators = {
  update: Actions.update,
};

const DEFAULT_EMPTY_ARRAY: [] = []; // don´t create new references

// SELECTORS
export const Selectors = {
  asset: (state: StoreState, ctx: Context.PiContext): Asset | undefined => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.data[contextId]?.asset;
  },
  clones: (state: StoreState, ctx: Context.PiContext): Asset[] => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.data[contextId]?.clones || DEFAULT_EMPTY_ARRAY;
  },
  relatedChannel: (state: StoreState, ctx: Context.PiContext): Channel | undefined => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.data[contextId]?.relatedChannel;
  },
  nextPrograms: (state: StoreState, ctx: Context.PiContext): Program[] => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.data[contextId]?.nextPrograms || DEFAULT_EMPTY_ARRAY;
  },
  options: (state: StoreState, ctx: Context.PiContext): AssetOption[] => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.data[contextId]?.options || DEFAULT_EMPTY_ARRAY;
  },
  related: (state: StoreState, ctx: Context.PiContext): PiRailUi[] => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.data[contextId]?.related || DEFAULT_EMPTY_ARRAY;
  },
  foldersRelated: (state: StoreState, ctx: Context.PiContext): PiRailUi[] => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.data[contextId]?.foldersRelated || DEFAULT_EMPTY_ARRAY;
  },
  seasons: (state: StoreState, ctx: Context.PiContext): PiRailUi[] => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.data[contextId]?.seasons || DEFAULT_EMPTY_ARRAY;
  },
  seasonsData: (state: StoreState, ctx: Context.PiContext): SeasonsData | undefined => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.data[contextId]?.seasonsData;
  },
  folders: (state: StoreState, ctx: Context.PiContext): Folder[] => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.data[contextId]?.folders || DEFAULT_EMPTY_ARRAY;
  },
  vodPrices: (state: StoreState, ctx: Context.PiContext): ProductPrice[] => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.data[contextId]?.vodPrices || DEFAULT_EMPTY_ARRAY;
  },
};

export interface PiRailUi {
  key: string;
  title: string;
  assets: Asset[];
  menuItem?: MenuItem;
}

interface PiRecord {
  asset?: Asset;
  clones?: Asset[];
  folders?: Folder[]; // folders related with clones subscriptions
  seasonsData?: SeasonsData;
  related?: PiRailUi[];
  foldersRelated?: PiRailUi[];
  seasons?: PiRailUi[];
  options?: AssetOption[];
  relatedChannel?: Channel;
  nextPrograms?: Program[];
  vodPrices?: ProductPrice[];
}

// REDUCER
type ReducerState = Record<string, PiRecord>;

const initialState: ReducerState = {};

export default function programmeInfo(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case Context.ActionType.START: {
      const contextId = Context.getContextId(action.meta);
      return {
        ...state,
        [contextId]: {},
      };
    }
    case ActionType.UPDATE: {
      const contextId = Context.getContextId(action.payload.context);
      return {
        ...state,
        [contextId]: {
          ...state[contextId],
          ...action.payload.data,
        },
      };
    }
    case Context.ActionType.CLEAR: {
      const contextId = Context.getContextId(action.meta);

      const newState = { ...state };
      delete newState[contextId];

      return newState;
    }
    default:
      return state;
  }
}
